'use client'

import { useState, useMemo } from 'react'
import Link from 'next/link'
import { Search, ArrowRight } from 'lucide-react'
import BrandLogo from '@/components/labs/BrandLogo'

interface BrandRow {
  brand: string
  brand_slug: string
  category: string
  domain: string
  unified_score: number | null
  unified_grade: string | null
  audited_at: string | null
}

function gradeColor(grade: string | null): string {
  switch (grade) {
    case 'A':
      return '#22c55e'
    case 'B':
      return '#84cc16'
    case 'C':
      return '#eab308'
    case 'D':
      return '#f97316'
    case 'F':
      return '#ef4444'
    default:
      return '#475569'
  }
}

export default function BrandSearchClient({ brands }: { brands: BrandRow[] }) {
  const [query, setQuery] = useState('')

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (q.length === 0) return []
    return brands
      .filter(
        b =>
          b.brand.toLowerCase().includes(q) ||
          b.domain.toLowerCase().includes(q)
      )
      .slice(0, 8)
  }, [brands, query])

  return (
    <section>
      <div className='relative'>
        <Search
          size={18}
          className='absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none'
        />
        <input
          type='text'
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder='Search any of the 50 brands in the index...'
          className='w-full rounded-xl border border-border bg-card pl-12 pr-4 py-4 text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[#008dcc] focus:ring-2 focus:ring-[#008dcc]/20 transition-colors'
          aria-label='Search brand index'
        />
      </div>

      {matches.length > 0 && (
        <ul className='mt-3 space-y-2'>
          {matches.map(b => (
            <li key={b.brand_slug}>
              <Link
                href={`/labs/brand-index/${b.brand_slug}`}
                className='flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 hover:border-foreground/30 transition-colors group'
              >
                <BrandLogo slug={b.brand_slug} brand={b.brand} size={28} />
                <span className='flex-1 font-semibold text-foreground'>
                  {b.brand}
                </span>
                <span className='text-xs text-muted-foreground capitalize'>
                  {b.category}
                </span>
                {b.unified_score === 0 ? (
                  <span className='text-xs font-semibold text-muted-foreground'>
                    blocked
                  </span>
                ) : b.unified_score != null ? (
                  <span
                    className='font-mono font-bold text-sm'
                    style={{ color: gradeColor(b.unified_grade) }}
                  >
                    {b.unified_score} ({b.unified_grade})
                  </span>
                ) : (
                  <span className='text-xs italic text-muted-foreground'>
                    pending
                  </span>
                )}
                <ArrowRight
                  size={14}
                  className='text-muted-foreground group-hover:text-foreground transition-colors'
                />
              </Link>
            </li>
          ))}
        </ul>
      )}

      {query.trim().length > 0 && matches.length === 0 && (
        <p className='mt-3 text-sm text-muted-foreground'>
          No brand matches &ldquo;{query}&rdquo;. The index is intentionally
          curated to 50 brands. To request a brand be added, email{' '}
          <a
            href='mailto:founders@pixelmojo.io?subject=Brand%20Index%20suggestion'
            className='underline hover:text-foreground'
          >
            founders@pixelmojo.io
          </a>
          .
        </p>
      )}
    </section>
  )
}
